import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pb } from '@/core/api';
import { useToast } from '@/components/ui/toast';
import type { ConfigEntry } from '../types';

export function useConfig(key: string) {
  const isAuth = !!pb.authStore.token;
  return useQuery({
    queryKey: ['config', key],
    queryFn: async (): Promise<ConfigEntry | null> => {
      return await pb
        .collection('config')
        .getFirstListItem<ConfigEntry>(`key = "${key}"`)
        .catch(() => null);
    },
    enabled: isAuth,
    staleTime: 10_000,
    retry: 1,
  });
}

async function upsertConfig(key: string, value: unknown): Promise<unknown> {
  try {
    const existing = await pb
      .collection('config')
      .getFirstListItem<ConfigEntry>(`key = "${key}"`);
    return pb.collection('config').update(existing.id, { value });
  } catch {
    return pb.collection('config').create({ key, value });
  }
}

export function useSetConfig() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: (v: { key: string; value: unknown }) =>
      upsertConfig(v.key, v.value),
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ['config', v.key] });
      qc.invalidateQueries({ queryKey: [v.key] });
      qc.invalidateQueries({ queryKey: ['leaderboard'] });
      toast('Configuración guardada', 'success');
    },
    onError: () => toast('Error al guardar', 'error'),
  });
}
